import { DEFAULT_PERIOD_DAYS } from "@app/components/agent_builder/observability/constants";
import type { PokeGetDatasourceRetrievalResponse } from "@app/lib/api/poke/agent_configurations";
import { emptyArray, useFetcher, useSWRWithDefaults } from "@app/lib/swr/swr";
import type { PokeGetAgentDetails } from "@app/types/api/poke/agent_configurations";
import type { LightWorkspaceType } from "@app/types/user";
import type { Fetcher } from "swr";

interface UsePokeAgentDetailsProps {
  disabled?: boolean;
  owner: LightWorkspaceType;
  aId: string;
}

export function usePokeAgentDetails({
  disabled,
  owner,
  aId,
}: UsePokeAgentDetailsProps) {
  const { fetcher } = useFetcher();
  const agentDetailsFetcher: Fetcher<PokeGetAgentDetails> = fetcher;
  const { data, error, mutate } = useSWRWithDefaults(
    `/api/poke/workspaces/${owner.sId}/agent_configurations/${aId}/details`,
    agentDetailsFetcher,
    { disabled }
  );

  return {
    data: data ?? null,
    isLoading: !error && !data && !disabled,
    isError: error,
    mutate,
  };
}

interface UsePokeAgentDatasourceRetrievalProps {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  disabled?: boolean;
}

export function usePokeAgentDatasourceRetrieval({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  disabled,
}: UsePokeAgentDatasourceRetrievalProps) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<PokeGetDatasourceRetrievalResponse> = fetcher;
  const params = new URLSearchParams({ days: days.toString() });
  const key = `/api/poke/workspaces/${workspaceId}/agent_configurations/${agentConfigurationId}/observability/datasource-retrieval?${params.toString()}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    datasourceRetrieval: data?.datasources ?? emptyArray(),
    totalRetrievals: data?.total ?? 0,
    isDatasourceRetrievalLoading: !error && !data && !disabled,
    isDatasourceRetrievalError: error,
    isDatasourceRetrievalValidating: isValidating,
  };
}
